import { Box, Text, Skeleton, Tooltip } from '@mantine/core'
import { IconArrowRight, IconInfoCircle } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'

const COLOR_MAP = {
  primary: 'var(--ai-brand-gradient)',
  info: '#098d93',
  success: '#2f9e44',
  warning: '#f59f00',
  danger: '#e03131',
  /** Sem cor de status: o valor nao foi medido, e nao ha o que afirmar sobre ele. */
  neutro: '#adb5bd',
} as const

interface SmallBoxProps {
  value: string | number
  label: string
  icon: React.ReactNode
  /**
   * Cor de status. **Omitir quando o valor nao veio.**
   *
   * Verde, amarelo e vermelho afirmam algo sobre o mundo — pintar de verde uma metrica que nao
   * chegou e dizer ao usuario que esta tudo bem sem ter olhado. Era o que acontecia com
   * `cpuColor(live.cpu_pct ?? 0)`: API fora do ar virava 0% de CPU em verde.
   */
  color?: keyof typeof COLOR_MAP
  linkTo?: string
  linkLabel?: string
  loading?: boolean
  onClick?: () => void
  /** Tooltip com contexto sobre a métrica (o que mede, como é calculada) */
  info?: string
}

export function SmallBox({ value, label, icon, color, linkTo, linkLabel, loading, onClick, info }: SmallBoxProps) {
  const navigate = useNavigate()
  const bg = COLOR_MAP[color ?? 'neutro']
  const isPrimary = color === 'primary'

  if (loading) return <Skeleton height={120} radius="md" />

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (linkTo) navigate(linkTo)
  }

  return (
    <Box
      onClick={onClick}
      style={{
        background: bg,
        borderRadius: 'var(--mantine-radius-md)',
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all var(--ai-transition-base)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.filter = 'brightness(1.08)'
        e.currentTarget.style.transform = 'translateY(-2px)'
        e.currentTarget.style.boxShadow = 'var(--ai-shadow-md)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.filter = 'brightness(1)'
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      {/* Content */}
      <Box style={{ padding: '16px 16px 10px', position: 'relative', zIndex: 1 }}>
        <Text size="xl" fw={700} c="white" lh={1.1}>{value}</Text>
        <Text size="sm" c="white" style={{ opacity: 0.85 }} mt={2}>{label}</Text>
      </Box>

      {/* Info tooltip */}
      {info && (
        <Tooltip
          label={info}
          multiline
          w={260}
          position="bottom"
          withArrow
          arrowSize={6}
          styles={{
            tooltip: {
              fontSize: 12,
              lineHeight: 1.4,
              padding: '10px 14px',
              backgroundColor: 'var(--mantine-color-dark-7, #1a1b1e)',
              color: '#c1c2c5',
            },
          }}
        >
          <Box
            style={{
              position: 'absolute',
              bottom: linkTo ? 34 : 10,
              right: 10,
              zIndex: 2,
              cursor: 'help',
              opacity: 0.5,
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9' }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5' }}
          >
            <IconInfoCircle size={16} color="white" />
          </Box>
        </Tooltip>
      )}

      {/* Icon overlay */}
      <Box
        style={{
          position: 'absolute',
          top: 8,
          right: 12,
          opacity: 0.3,
          zIndex: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box style={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
          {icon}
        </Box>
      </Box>

      {/* Footer link */}
      {linkTo && (
        <Box
          onClick={handleLinkClick}
          style={{
            backgroundColor: 'rgba(0,0,0,0.15)',
            padding: '6px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            cursor: 'pointer',
            transition: 'background-color var(--ai-transition-base)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.25)' }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.15)' }}
        >
          <Text size="xs" c="white" fw={500}>{linkLabel ?? 'More info'}</Text>
          <IconArrowRight size={12} color="white" />
        </Box>
      )}
    </Box>
  )
}
